import type { FC } from 'react';

interface ExperimentIllustrationProps {
  type: string;
}

/**
 * All SVGs use the Good Atoms design system palette:
 *   Teal       #00C2A8 / rgba(0,194,168,…)   — glass borders, accents
 *   Blue       #378ADD / rgba(55,138,221,…)   — water, inputs
 *   Amber      #EF9F27 / rgba(239,159,39,…)   — energy, heat, light
 *   Green      #639922 / #16A34A               — life, biology
 *   Sand       #F5EDD8                         — warm fills (egg, beaker bg)
 *   Ink        #1E1E2E                          — labels, outlines
 *   Ink-muted  #64748B                          — secondary labels
 */

// --- 1. Egg dissolving in vinegar ---
const EggVinegar: FC = () => (
  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes bubble-rise {
        0% { transform: translateY(0); opacity: 0.8; }
        100% { transform: translateY(-60px); opacity: 0; }
      }
      @keyframes shell-fade {
        0%, 50% { stroke-dasharray: 0 0; opacity: 1; }
        100% { stroke-dasharray: 3 4; opacity: 0.4; }
      }
      .ev-bubble { animation: bubble-rise 2.5s ease-out infinite; }
      .ev-bubble-2 { animation: bubble-rise 3s ease-out infinite 0.8s; }
      .ev-bubble-3 { animation: bubble-rise 2s ease-out infinite 1.4s; }
      .ev-bubble-4 { animation: bubble-rise 2.8s ease-out infinite 0.4s; }
      .ev-shell { animation: shell-fade 6s ease-in-out infinite; }
    `}</style>

    {/* Beaker */}
    <rect x="50" y="40" width="100" height="130" rx="12" fill="rgba(239,159,39,0.07)" stroke="#00C2A8" strokeWidth="2.5" />
    <rect x="40" y="36" width="120" height="10" rx="3" fill="none" stroke="#00C2A8" strokeWidth="2.5" />

    {/* Liquid surface line */}
    <line x1="52" y1="55" x2="148" y2="55" stroke="#EF9F27" strokeWidth="1" opacity="0.5" />

    {/* Egg */}
    <ellipse cx="100" cy="120" rx="22" ry="28" fill="#F5EDD8" />
    <ellipse cx="100" cy="120" rx="22" ry="28" fill="none" stroke="#C8B89A" strokeWidth="1.5" className="ev-shell" />

    {/* Bubbles — amber (CO₂) */}
    <circle cx="88" cy="100" r="3" fill="#EF9F27" opacity="0.6" className="ev-bubble" />
    <circle cx="108" cy="105" r="2.5" fill="#EF9F27" opacity="0.5" className="ev-bubble-2" />
    <circle cx="95" cy="95" r="2" fill="#EF9F27" opacity="0.6" className="ev-bubble-3" />
    <circle cx="112" cy="98" r="3.5" fill="#EF9F27" opacity="0.4" className="ev-bubble-4" />
    <circle cx="82" cy="108" r="2" fill="#EF9F27" opacity="0.5" className="ev-bubble-2" />

    {/* Label */}
    <text x="100" y="190" textAnchor="middle" fontSize="11" fill="#64748B" fontFamily="Inter, sans-serif" fontWeight="600">Egg + Eddik</text>
  </svg>
);

// --- 2. Light splitting through water (rainbow) ---
const Rainbow: FC = () => (
  <svg viewBox="0 0 300 150" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes beam-in {
        0% { stroke-dashoffset: 80; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes spectrum-fan {
        0% { opacity: 0; stroke-dashoffset: 100; }
        40% { opacity: 0; stroke-dashoffset: 100; }
        100% { opacity: 1; stroke-dashoffset: 0; }
      }
      .rb-beam { animation: beam-in 2s ease-out infinite; stroke-dasharray: 80; }
      .rb-spec { stroke-dasharray: 100; animation: spectrum-fan 3s ease-out infinite; }
    `}</style>

    {/* Flashlight body */}
    <rect x="10" y="58" width="50" height="24" rx="4" fill="#64748B" />
    <rect x="55" y="62" width="12" height="16" rx="2" fill="#EF9F27" />

    {/* Incoming beam */}
    <line x1="67" y1="70" x2="130" y2="70" stroke="#EF9F27" strokeWidth="4" opacity="0.9" className="rb-beam" />

    {/* Glass of water */}
    <rect x="130" y="40" width="40" height="80" rx="4" fill="rgba(55,138,221,0.08)" stroke="#00C2A8" strokeWidth="2" />
    <rect x="132" y="55" width="36" height="63" rx="2" fill="rgba(55,138,221,0.18)" />

    {/* Rainbow spectrum lines */}
    {[
      { color: '#EF4444', y: 42 },
      { color: '#F97316', y: 50 },
      { color: '#EAB308', y: 58 },
      { color: '#16A34A', y: 66 },
      { color: '#2563EB', y: 74 },
      { color: '#4F46E5', y: 82 },
      { color: '#7C3AED', y: 90 },
    ].map((ray, i) => (
      <line
        key={i}
        x1="170"
        y1="70"
        x2="285"
        y2={ray.y}
        stroke={ray.color}
        strokeWidth="2.5"
        className="rb-spec"
        style={{ animationDelay: `${1.2 + i * 0.12}s` }}
      />
    ))}

    {/* Label */}
    <text x="150" y="140" textAnchor="middle" fontSize="10" fill="#64748B" fontFamily="Inter, sans-serif" fontWeight="600">Lys + Vann = Regnbue</text>
  </svg>
);

// --- 3. Floating egg density experiment ---
const FloatingEgg: FC = () => (
  <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes egg-bob {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      .fe-bob { animation: egg-bob 2.5s ease-in-out infinite; }
    `}</style>

    {/* Left glass — plain water */}
    <rect x="30" y="50" width="90" height="120" rx="10" fill="rgba(55,138,221,0.05)" stroke="#00C2A8" strokeWidth="2.5" />
    <rect x="32" y="70" width="86" height="98" rx="8" fill="rgba(55,138,221,0.18)" />
    {/* Egg sinking */}
    <ellipse cx="75" cy="145" rx="16" ry="20" fill="#F5EDD8" stroke="#C8B89A" strokeWidth="1.5" />

    {/* Right glass — salt water */}
    <rect x="180" y="50" width="90" height="120" rx="10" fill="rgba(55,138,221,0.05)" stroke="#00C2A8" strokeWidth="2.5" />
    <rect x="182" y="70" width="86" height="98" rx="8" fill="rgba(55,138,221,0.22)" />
    {/* Salt granules */}
    {[
      [200, 155], [215, 160], [230, 150], [245, 158], [208, 145],
      [238, 142], [220, 140], [195, 148], [250, 148], [210, 152],
    ].map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="1.4" fill="rgba(255,255,255,0.9)" />
    ))}
    {/* Floating egg */}
    <g className="fe-bob">
      <ellipse cx="225" cy="90" rx="16" ry="20" fill="#F5EDD8" stroke="#C8B89A" strokeWidth="1.5" />
    </g>

    {/* Labels */}
    <text x="75" y="190" textAnchor="middle" fontSize="11" fill="#1E1E2E" fontFamily="Inter, sans-serif" fontWeight="700">Vann</text>
    <text x="225" y="190" textAnchor="middle" fontSize="11" fill="#1E1E2E" fontFamily="Inter, sans-serif" fontWeight="700">Saltvann</text>

    {/* Down arrow left */}
    <line x1="75" y1="105" x2="75" y2="122" stroke="#EF4444" strokeWidth="2" markerEnd="url(#fe-arrow)" />
    {/* Up arrow right */}
    <line x1="225" y1="115" x2="225" y2="98" stroke="#16A34A" strokeWidth="2" markerEnd="url(#fe-arrow-up)" />

    <defs>
      <marker id="fe-arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="#EF4444" />
      </marker>
      <marker id="fe-arrow-up" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="#16A34A" />
      </marker>
    </defs>
  </svg>
);

// --- 4. Solar oven ---
const SolarOven: FC = () => (
  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes sun-ray-move {
        0% { stroke-dashoffset: 20; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes heat-wave {
        0% { transform: translateY(0); opacity: 0.6; }
        100% { transform: translateY(-20px); opacity: 0; }
      }
      .so-ray { stroke-dasharray: 6 4; animation: sun-ray-move 1.5s linear infinite; }
      .so-heat { animation: heat-wave 2s ease-out infinite; }
      .so-heat-2 { animation: heat-wave 2.4s ease-out infinite 0.6s; }
      .so-heat-3 { animation: heat-wave 2.2s ease-out infinite 1.2s; }
    `}</style>

    {/* Sun */}
    <circle cx="165" cy="25" r="18" fill="#EF9F27" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      return (
        <line
          key={i}
          x1={165 + Math.cos(rad) * 22}
          y1={25 + Math.sin(rad) * 22}
          x2={165 + Math.cos(rad) * 30}
          y2={25 + Math.sin(rad) * 30}
          stroke="#EF9F27"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.6"
        />
      );
    })}

    {/* Sun rays to box */}
    <line x1="150" y1="42" x2="115" y2="80" stroke="#EF9F27" strokeWidth="2" className="so-ray" opacity="0.7" />
    <line x1="155" y1="45" x2="125" y2="80" stroke="#EF9F27" strokeWidth="2" className="so-ray" opacity="0.7" />
    <line x1="145" y1="40" x2="105" y2="80" stroke="#EF9F27" strokeWidth="2" className="so-ray" opacity="0.7" />

    {/* Box body */}
    <rect x="40" y="80" width="120" height="80" rx="4" fill="#F5EDD8" stroke="#00C2A8" strokeWidth="2" />

    {/* Lid (angled reflector) */}
    <polygon points="40,80 100,45 160,80" fill="rgba(0,194,168,0.15)" stroke="#00C2A8" strokeWidth="1.5" />

    {/* Food item */}
    <circle cx="100" cy="130" r="10" fill="#639922" />
    <circle cx="100" cy="130" r="6" fill="#16A34A" />

    {/* Heat waves */}
    <path d="M85,115 Q90,108 95,115" fill="none" stroke="#EF4444" strokeWidth="1.2" opacity="0.5" className="so-heat" />
    <path d="M100,112 Q105,105 110,112" fill="none" stroke="#EF4444" strokeWidth="1.2" opacity="0.5" className="so-heat-2" />
    <path d="M115,115 Q120,108 125,115" fill="none" stroke="#EF4444" strokeWidth="1.2" opacity="0.5" className="so-heat-3" />

    <text x="100" y="185" textAnchor="middle" fontSize="11" fill="#64748B" fontFamily="Inter, sans-serif" fontWeight="600">Solovn</text>
  </svg>
);

// --- 5. Seed growth / sprouting ---
const SeedGrowth: FC = () => (
  <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes root-grow {
        0% { stroke-dashoffset: 50; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes sprout-grow {
        0% { stroke-dashoffset: 80; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes sun-pulse {
        0%, 100% { opacity: 0.85; }
        50% { opacity: 1; }
      }
      .sg-root { stroke-dasharray: 50; animation: root-grow 4s ease-out infinite; }
      .sg-sprout { stroke-dasharray: 80; animation: sprout-grow 4s ease-out infinite; }
      .sg-sun { animation: sun-pulse 3s ease-in-out infinite; }
    `}</style>

    <g className="sg-sun">
      <circle cx="100" cy="28" r="16" fill="#EF9F27" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        return (
          <line key={i}
            x1={100 + Math.cos(rad) * 20} y1={28 + Math.sin(rad) * 20}
            x2={100 + Math.cos(rad) * 28} y2={28 + Math.sin(rad) * 28}
            stroke="#EF9F27" strokeWidth="2" strokeLinecap="round" opacity="0.6"
          />
        );
      })}
    </g>

    {/* Ground */}
    <rect x="0" y="110" width="200" height="140" fill="rgba(120,53,15,0.12)" />
    <line x1="0" y1="110" x2="200" y2="110" stroke="#78350F" strokeWidth="2" opacity="0.4" />

    {/* Seed */}
    <ellipse cx="100" cy="140" rx="10" ry="7" fill="#78350F" opacity="0.8" />

    {/* Roots */}
    <path d="M100,147 Q95,170 88,195" fill="none" stroke="#78350F" strokeWidth="2" strokeLinecap="round" className="sg-root" opacity="0.6" />
    <path d="M100,147 Q108,175 115,200" fill="none" stroke="#78350F" strokeWidth="2" strokeLinecap="round" className="sg-root" style={{ animationDelay: '0.5s' }} opacity="0.6" />

    {/* Sprout stem */}
    <path d="M100,140 Q100,110 100,70" fill="none" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" className="sg-sprout" />

    {/* Leaves */}
    <ellipse cx="88" cy="80" rx="12" ry="5" fill="#16A34A" transform="rotate(-30 88 80)" />
    <ellipse cx="112" cy="88" rx="12" ry="5" fill="#16A34A" transform="rotate(30 112 88)" />
    <ellipse cx="92" cy="68" rx="9" ry="4" fill="#00C2A8" transform="rotate(-40 92 68)" />
    <ellipse cx="108" cy="68" rx="9" ry="4" fill="#00C2A8" transform="rotate(40 108 68)" />

    <text x="100" y="240" textAnchor="middle" fontSize="11" fill="#64748B" fontFamily="Inter, sans-serif" fontWeight="600">Frospiring</text>
  </svg>
);

// --- 6. Bridge with forces ---
const Bridge: FC = () => (
  <svg viewBox="0 0 300 150" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
    <style>{`
      @keyframes force-pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 1; }
      }
      @keyframes weight-press {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(3px); }
      }
      .br-force { animation: force-pulse 2s ease-in-out infinite; }
      .br-weight { animation: weight-press 2s ease-in-out infinite; }
    `}</style>

    {/* Ground */}
    <line x1="10" y1="120" x2="290" y2="120" stroke="#1E1E2E" strokeWidth="2" opacity="0.2" />

    {/* Pillars */}
    <rect x="35" y="70" width="20" height="50" fill="#64748B" rx="2" opacity="0.7" />
    <rect x="245" y="70" width="20" height="50" fill="#64748B" rx="2" opacity="0.7" />

    {/* Bridge deck */}
    <rect x="45" y="65" width="210" height="8" fill="#EF9F27" rx="1" opacity="0.9" />

    {/* Truss triangles — teal */}
    <g fill="none" stroke="#00C2A8" strokeWidth="2">
      <polygon points="55,65 90,35 125,65" />
      <polygon points="125,65 150,35 175,65" />
      <polygon points="175,65 210,35 245,65" />
      <line x1="90" y1="35" x2="150" y2="35" />
      <line x1="150" y1="35" x2="210" y2="35" />
    </g>

    {/* Weight arrow */}
    <g className="br-weight">
      <rect x="140" y="8" width="20" height="14" rx="3" fill="#1E1E2E" opacity="0.7" />
      <line x1="150" y1="22" x2="150" y2="33" stroke="#EF4444" strokeWidth="2" />
      <polygon points="145,33 150,40 155,33" fill="#EF4444" />
    </g>

    {/* Force distribution arrows — blue */}
    <g className="br-force">
      <line x1="80" y1="48" x2="68" y2="58" stroke="#378ADD" strokeWidth="2" markerEnd="url(#br-arr)" />
      <line x1="220" y1="48" x2="232" y2="58" stroke="#378ADD" strokeWidth="2" markerEnd="url(#br-arr)" />
      <line x1="45" y1="78" x2="45" y2="95" stroke="#378ADD" strokeWidth="2" markerEnd="url(#br-arr)" />
      <line x1="255" y1="78" x2="255" y2="95" stroke="#378ADD" strokeWidth="2" markerEnd="url(#br-arr)" />
    </g>

    <defs>
      <marker id="br-arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
        <path d="M0,0 L6,3 L0,6 Z" fill="#378ADD" />
      </marker>
    </defs>

    <text x="150" y="143" textAnchor="middle" fontSize="10" fill="#64748B" fontFamily="Inter, sans-serif" fontWeight="600">Krefter i en bro</text>
  </svg>
);

// --- Illustration map ---
const ILLUSTRATIONS: Record<string, FC> = {
  'egg-vinegar': EggVinegar,
  'rainbow': Rainbow,
  'floating-egg': FloatingEgg,
  'solar-oven': SolarOven,
  'seed-growth': SeedGrowth,
  'bridge': Bridge,
};

export function ExperimentIllustration({ type }: ExperimentIllustrationProps) {
  const Illustration = ILLUSTRATIONS[type];
  if (!Illustration) return null;
  return (
    <div className="my-8 max-w-sm mx-auto">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-card p-6 flex justify-center">
        <Illustration />
      </div>
    </div>
  );
}

export default ExperimentIllustration;
